import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { Plus, RefreshCw, Search, Trash2, ChevronUp, ChevronDown, Package, CheckCircle2, XCircle, Loader2 } from 'lucide-react';
import { adminAPI } from '../../services/api';
import Toast from '../../components/admin/Toast';
import ConfirmDialog from '../../components/admin/ConfirmDialog';
import '../../styles/AdminGlobal.css';
import '../../styles/AdminShared.css';
import './Admin.css';

interface ProductLink {
  id?: number;
  product: number;
  product_name: string;
  product_name_bn: string;
  product_image: string | null;
  product_price: string;
  campaign_id: string;
  campaign_name: string;
  ad_set_id: string;
  ad_set_name: string;
  is_active: boolean;
  created_at?: string;
}

interface SearchProduct {
  id: number;
  name_en: string;
  name_bn: string;
  price: string;
  image: string | null;
}

interface Campaign {
  id: string;
  name: string;
  status: string;
}

const CampaignProducts: React.FC = () => {
  const [links, setLinks] = useState<ProductLink[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortField, setSortField] = useState('id');
  const [sortDir, setSortDir] = useState<'asc'|'desc'>('desc');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [toast, setToast] = useState<{show: boolean, message: string, type: 'success' | 'error'}>({
    show: false, message: '', type: 'success'
  });
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [selectedLink, setSelectedLink] = useState<ProductLink | null>(null);

  // Form state
  const [productSearch, setProductSearch] = useState('');
  const [searchResults, setSearchResults] = useState<SearchProduct[]>([]);
  const [searching, setSearching] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState<SearchProduct | null>(null);
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [loadingCampaigns, setLoadingCampaigns] = useState(false);
  const [selectedCampaign, setSelectedCampaign] = useState<Campaign | null>(null);
  const [saving, setSaving] = useState(false);
  const [noConfig, setNoConfig] = useState(false);

  const loadLinks = useCallback(async () => {
    try {
      setLoading(true);
      const response = await adminAPI.getCampaignProducts();
      let data = response.data || response;
      if (data?.results && Array.isArray(data.results)) data = data.results;
      setLinks(Array.isArray(data) ? data : (data ? [data] : []));
    } catch (error) {
      console.error('Failed to load campaign products:', error);
      setLinks([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadLinks(); }, [loadLinks]);

  const handleDeleteClick = (link: ProductLink) => {
    setSelectedLink(link);
    setIsDeleteDialogOpen(true);
  };

  const handleDelete = async () => {
    if (!selectedLink) return;
    try {
      await adminAPI.deleteCampaignProduct(selectedLink.id!);
      setToast({ show: true, message: 'Product unlinked from campaign', type: 'success' });
      loadLinks();
    } catch (error) {
      setToast({ show: true, message: 'Failed to remove link', type: 'error' });
    } finally {
      setIsDeleteDialogOpen(false);
      setSelectedLink(null);
    }
  };

  const handleAddNew = async () => {
    setIsFormOpen(true);
    setSelectedProduct(null);
    setSelectedCampaign(null);
    setProductSearch('');
    setSearchResults([]);
    setCampaigns([]);
    setNoConfig(false);

    // Load campaigns
    setLoadingCampaigns(true);
    try {
      const response = await adminAPI.getMetaAdsCampaigns('last_30d');
      const data = response.data || response;
      setCampaigns(data.campaigns || []);
    } catch (error: any) {
      if (error.response?.status === 404) {
        setNoConfig(true);
      } else {
        setToast({ show: true, message: 'Failed to load campaigns from Meta', type: 'error' });
      }
    } finally {
      setLoadingCampaigns(false);
    }
  };

  // Debounce product search
  useEffect(() => {
    if (productSearch.length < 2) { setSearchResults([]); return; }
    const timer = setTimeout(async () => {
      setSearching(true);
      try {
        const response = await adminAPI.searchCampaignProducts(productSearch);
        setSearchResults(response.data || response || []);
      } catch {
        setSearchResults([]);
      } finally {
        setSearching(false);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [productSearch]);

  const handleSave = async () => {
    if (!selectedProduct || !selectedCampaign) {
      setToast({ show: true, message: 'Please select both a product and a campaign', type: 'error' });
      return;
    }

    try {
      setSaving(true);
      await adminAPI.createCampaignProduct({
        product: selectedProduct.id,
        campaign_id: selectedCampaign.id,
        campaign_name: selectedCampaign.name,
        is_active: true,
      });
      setToast({ show: true, message: `"${selectedProduct.name_en}" linked to "${selectedCampaign.name}"`, type: 'success' });
      setIsFormOpen(false);
      loadLinks();
    } catch (error: any) {
      const detail = error.response?.data;
      let msg = 'Failed to link product';
      if (detail?.non_field_errors) msg = detail.non_field_errors[0];
      else if (detail && typeof detail === 'object') {
        const first = Object.values(detail).flat()[0];
        if (first) msg = String(first);
      }
      setToast({ show: true, message: msg, type: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const filteredLinks = useMemo(() => {
    let list = links.filter(link =>
      link.product_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      link.campaign_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      link.campaign_id?.includes(searchTerm)
    );
    list = [...list].sort((a: any, b: any) => {
      const av = a[sortField] ?? ''; const bv = b[sortField] ?? '';
      if (typeof av === 'number' && typeof bv === 'number') return sortDir === 'asc' ? av - bv : bv - av;
      return sortDir === 'asc' ? String(av).localeCompare(String(bv)) : String(bv).localeCompare(String(av));
    });
    return list;
  }, [links, searchTerm, sortField, sortDir]);
  const totalPages = Math.max(1, Math.ceil(filteredLinks.length / pageSize));
  const paginated = filteredLinks.slice((currentPage - 1) * pageSize, currentPage * pageSize);
  const pageNums = (() => {
    const pages: (number | '...')[] = [];
    if (totalPages <= 7) { for (let i = 1; i <= totalPages; i++) pages.push(i); return pages; }
    pages.push(1); if (currentPage > 3) pages.push('...');
    for (let i = Math.max(2, currentPage - 1); i <= Math.min(totalPages - 1, currentPage + 1); i++) pages.push(i);
    if (currentPage < totalPages - 2) pages.push('...'); pages.push(totalPages); return pages;
  })();
  const handleSort = (f: string) => {
    if (sortField === f) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortField(f); setSortDir('asc'); }
    setCurrentPage(1);
  };
  const SA = ({ field }: { field: string }) => {
    if (sortField !== field) return null;
    return sortDir === 'asc' ? <ChevronUp size={10} className="adm-sort-arrow" /> : <ChevronDown size={10} className="adm-sort-arrow" />;
  };

  return (
    <div className="admin-page adm-page">
      <div className="adm-topbar">
        <h1 className="adm-page-title">Campaign Products</h1>
        <div className="adm-topbar-right">
          <button className="adm-btn adm-btn-add" onClick={handleAddNew}>
            <Plus size={13} /> Link Product <span className="adm-btn-badge">{links.length}</span>
          </button>
          <button className="adm-btn adm-btn-refresh" onClick={loadLinks} disabled={loading}>
            {loading ? <span className="adm-spin"><Loader2 size={13} /></span> : <RefreshCw size={13} />} Refresh
          </button>
        </div>
      </div>
      <div className="adm-filter-wrap">
        <div className="adm-filter-left">
          <span className="adm-count"><strong>{filteredLinks.length}</strong> link(s)</span>
        </div>
        <div className="adm-filter-right">
          <div className="adm-search-wrap">
            <span className="adm-search-icon"><Search size={13} /></span>
            <input className="adm-search-input" placeholder="Search by product or campaign…" value={searchTerm} onChange={e => { setSearchTerm(e.target.value); setCurrentPage(1); }} />
          </div>
        </div>
      </div>
      <div className="adm-table-card">
        <div className="adm-table-scroll">
          <table className="adm-table">
            <thead>
              <tr>
                <th className="adm-th" style={{ width: 50 }}>Image</th>
                <th className="adm-th adm-th-sort" onClick={() => handleSort('product_name')}>Product <SA field="product_name" /></th>
                <th className="adm-th adm-th-sort" onClick={() => handleSort('campaign_name')}>Campaign <SA field="campaign_name" /></th>
                <th className="adm-th">Campaign ID</th>
                <th className="adm-th">Ad Set</th>
                <th className="adm-th adm-th-sort" onClick={() => handleSort('product_price')}>Price <SA field="product_price" /></th>
                <th className="adm-th adm-th-sort" onClick={() => handleSort('is_active')}>Status <SA field="is_active" /></th>
                <th className="adm-th" style={{ textAlign: 'center' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan={8} className="adm-loading"><span className="adm-spin" style={{ marginRight: 8 }}><Loader2 size={18} /></span>Loading…</td></tr>}
              {!loading && paginated.length === 0 && <tr><td colSpan={8} className="adm-empty">No product-campaign links found.</td></tr>}
              {!loading && paginated.map((link: ProductLink) => (
                <tr className="adm-row" key={link.id}>
                  <td className="adm-td" style={{ textAlign: 'center' }}>
                    {link.product_image
                      ? <img src={link.product_image} alt="" className="adm-img" />
                      : <div className="adm-img-placeholder"><Package size={16} /></div>}
                  </td>
                  <td className="adm-td"><span className="adm-name-main">{link.product_name}</span></td>
                  <td className="adm-td"><span style={{ color: '#4f46e5', fontWeight: 600, fontSize: 12 }}>{link.campaign_name}</span></td>
                  <td className="adm-td"><span className="adm-mono">{link.campaign_id}</span></td>
                  <td className="adm-td"><span className="adm-text-muted">{link.ad_set_name}</span></td>
                  <td className="adm-td"><span className="adm-price-main" style={{ fontSize: 12 }}>৳{link.product_price}</span></td>
                  <td className="adm-td">
                    <span className={`adm-chip ${link.is_active ? 'adm-chip-green' : 'adm-chip-red'}`}>
                      {link.is_active ? <><CheckCircle2 size={9} /> Active</> : <><XCircle size={9} /> Inactive</>}
                    </span>
                  </td>
                  <td className="adm-td" style={{ textAlign: 'center', padding: '6px 4px' }}>
                    <div className="adm-actions-row">
                      <button className="adm-action-btn adm-ab-del" onClick={() => handleDeleteClick(link)} title="Remove"><Trash2 size={12} /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="adm-pagination">
          <div className="adm-page-size-wrap"><span>Show</span><select className="adm-page-size-select" value={pageSize} onChange={e => { setPageSize(Number(e.target.value)); setCurrentPage(1); }}>{[10,20,50].map(n => <option key={n} value={n}>{n}</option>)}</select><span>per page</span></div>
          <div className="adm-page-info">{filteredLinks.length === 0 ? '0 links' : `${(currentPage-1)*pageSize+1}–${Math.min(currentPage*pageSize,filteredLinks.length)} of ${filteredLinks.length}`}</div>
          <div className="adm-page-controls">
            <button className="adm-page-btn" disabled={currentPage===1} onClick={() => setCurrentPage(p => p-1)}>‹</button>
            <div className="adm-page-numbers">{pageNums.map((n,i) => n==='...' ? <span key={`e${i}`} style={{padding:'0 4px',color:'#94a3b8'}}>…</span> : <button key={n} className={`adm-page-num${currentPage===n?' active':''}`} onClick={() => setCurrentPage(n as number)}>{n}</button>)}</div>
            <button className="adm-page-btn" disabled={currentPage===totalPages} onClick={() => setCurrentPage(p => p+1)}>›</button>
          </div>
        </div>
      </div>

      {/* Link Product Modal */}
      {isFormOpen && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div style={{ background: 'white', borderRadius: '12px', padding: '28px', width: '560px', maxHeight: '85vh', overflow: 'auto', boxShadow: '0 25px 50px rgba(0,0,0,0.25)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', borderBottom: '1px solid #e5e7eb', paddingBottom: '12px' }}>
              <h3 style={{ margin: 0, color: '#1f2937', fontSize: '18px' }}>Link Product to Campaign</h3>
              <button onClick={() => setIsFormOpen(false)} style={{ background: 'none', border: 'none', fontSize: '24px', cursor: 'pointer', color: '#6b7280' }}>×</button>
            </div>

            {noConfig ? (
              <div style={{ textAlign: 'center', padding: '40px 20px' }}>
                <div style={{ fontSize: '48px', marginBottom: '12px' }}>📡</div>
                <p style={{ color: '#6b7280', marginBottom: '16px' }}>Configure your Meta Ads credentials first to load campaigns.</p>
                <a href="/admin/meta-ads/config" style={{ color: '#2563eb', fontWeight: '600', textDecoration: 'none' }}>Go to Meta Ads Config →</a>
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                {/* Product Search */}
                <div>
                  <label style={{ display: 'block', marginBottom: '6px', fontWeight: '600', fontSize: '13px', color: '#374151' }}>
                    Search Product <span style={{ color: '#dc2626' }}>*</span>
                  </label>
                  <input
                    type="text"
                    value={productSearch}
                    onChange={(e) => { setProductSearch(e.target.value); setSelectedProduct(null); }}
                    placeholder="Type product name to search..."
                    style={{ width: '100%', padding: '10px 12px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '14px', outline: 'none', boxSizing: 'border-box' }}
                  />

                  {/* Search Results */}
                  {searching && <div style={{ padding: '8px', color: '#6b7280', fontSize: '13px' }}>Searching...</div>}
                  {searchResults.length > 0 && !selectedProduct && (
                    <div style={{ border: '1px solid #e5e7eb', borderRadius: '6px', marginTop: '4px', maxHeight: '200px', overflow: 'auto', background: 'white' }}>
                      {searchResults.map(p => (
                        <div key={p.id} onClick={() => {
                          setSelectedProduct(p);
                          setProductSearch(p.name_en);
                          setSearchResults([]);
                        }}
                          style={{ padding: '10px 12px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '10px', borderBottom: '1px solid #f3f4f6', transition: 'background 0.15s' }}
                          onMouseEnter={(e) => (e.currentTarget.style.background = '#f9fafb')}
                          onMouseLeave={(e) => (e.currentTarget.style.background = 'white')}
                        >
                          {p.image ? (
                            <img src={p.image} alt="" style={{ width: '36px', height: '36px', borderRadius: '6px', objectFit: 'cover' }} />
                          ) : (
                            <div style={{ width: '36px', height: '36px', borderRadius: '6px', background: '#f3f4f6', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>📦</div>
                          )}
                          <div style={{ flex: 1 }}>
                            <div style={{ fontWeight: '600', fontSize: '13px', color: '#1f2937' }}>{p.name_en}</div>
                            <div style={{ fontSize: '11px', color: '#6b7280' }}>৳{p.price}</div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Selected Product Badge */}
                  {selectedProduct && (
                    <div style={{ marginTop: '8px', display: 'flex', alignItems: 'center', gap: '8px', background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: '8px', padding: '8px 12px' }}>
                      {selectedProduct.image && <img src={selectedProduct.image} alt="" style={{ width: '32px', height: '32px', borderRadius: '6px', objectFit: 'cover' }} />}
                      <div style={{ flex: 1 }}>
                        <div style={{ fontWeight: '600', fontSize: '13px', color: '#1e40af' }}>{selectedProduct.name_en}</div>
                        <div style={{ fontSize: '11px', color: '#3b82f6' }}>৳{selectedProduct.price}</div>
                      </div>
                      <button onClick={() => { setSelectedProduct(null); setProductSearch(''); }}
                        style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#6b7280', fontSize: '18px' }}>×</button>
                    </div>
                  )}
                </div>

                {/* Campaign Selection */}
                <div>
                  <label style={{ display: 'block', marginBottom: '6px', fontWeight: '600', fontSize: '13px', color: '#374151' }}>
                    Select Campaign <span style={{ color: '#dc2626' }}>*</span>
                  </label>
                  {loadingCampaigns ? (
                    <div style={{ padding: '12px', color: '#6b7280', fontSize: '13px', textAlign: 'center' }}>Loading campaigns from Meta...</div>
                  ) : campaigns.length === 0 ? (
                    <div style={{ padding: '12px', color: '#6b7280', fontSize: '13px', textAlign: 'center', background: '#f9fafb', borderRadius: '6px' }}>No campaigns found in your Meta account</div>
                  ) : (
                    <div style={{ border: '1px solid #d1d5db', borderRadius: '6px', maxHeight: '240px', overflow: 'auto' }}>
                      {campaigns.map(c => (
                        <div key={c.id} onClick={() => setSelectedCampaign(c)}
                          style={{
                            padding: '10px 14px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '10px',
                            borderBottom: '1px solid #f3f4f6', transition: 'background 0.15s',
                            background: selectedCampaign?.id === c.id ? '#eff6ff' : 'white',
                            borderLeft: selectedCampaign?.id === c.id ? '3px solid #2563eb' : '3px solid transparent'
                          }}
                          onMouseEnter={(e) => { if (selectedCampaign?.id !== c.id) e.currentTarget.style.background = '#f9fafb'; }}
                          onMouseLeave={(e) => { if (selectedCampaign?.id !== c.id) e.currentTarget.style.background = 'white'; }}
                        >
                          <div style={{ flex: 1 }}>
                            <div style={{ fontWeight: '600', fontSize: '13px', color: '#1f2937' }}>{c.name}</div>
                            <div style={{ fontSize: '11px', color: '#6b7280' }}>ID: {c.id}</div>
                          </div>
                          <span style={{
                            padding: '2px 8px', borderRadius: '10px', fontSize: '11px', fontWeight: '600',
                            background: c.status === 'ACTIVE' ? '#d1fae5' : '#fef3c7',
                            color: c.status === 'ACTIVE' ? '#059669' : '#d97706'
                          }}>{c.status}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}

            {!noConfig && (
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '24px', paddingTop: '16px', borderTop: '1px solid #e5e7eb' }}>
                <button onClick={() => setIsFormOpen(false)}
                  style={{ padding: '8px 20px', borderRadius: '6px', border: '1px solid #d1d5db', background: 'white', color: '#374151', cursor: 'pointer', fontSize: '14px' }}>
                  Cancel
                </button>
                <button onClick={handleSave} disabled={saving || !selectedProduct || !selectedCampaign}
                  style={{
                    padding: '8px 20px', borderRadius: '6px', border: 'none',
                    background: saving || !selectedProduct || !selectedCampaign ? '#93c5fd' : '#2563eb',
                    color: 'white', cursor: saving || !selectedProduct || !selectedCampaign ? 'not-allowed' : 'pointer',
                    fontSize: '14px', fontWeight: '600'
                  }}>
                  {saving ? 'Linking...' : 'Link Product'}
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Delete Confirmation */}
      <ConfirmDialog
        isOpen={isDeleteDialogOpen}
        title="Remove Product Link"
        message={`Are you sure you want to unlink "${selectedLink?.product_name}" from campaign "${selectedLink?.campaign_name}"?`}
        onConfirm={handleDelete}
        onClose={() => setIsDeleteDialogOpen(false)}
        confirmText="Remove"
        cancelText="Cancel"
        type="danger"
      />

      <Toast show={toast.show} message={toast.message} type={toast.type} onClose={() => setToast({ ...toast, show: false })} />
    </div>
  );
};

export default CampaignProducts;
